import React, { useEffect, useState } from 'react';
import { Locale, ViewMode } from './types';
import { content } from './data/content';
import { Hero } from './components/Hero';
import { ProofScale } from './components/ProofScale';
import { SelectedWork } from './components/SelectedWork';
import { PushIncidentFlow } from './components/PushIncidentFlow';
import { FullExperience } from './components/FullExperience';
import { Capabilities } from './components/Capabilities';
import { Cases } from './components/Cases';
import { Principles } from './components/Principles';
import { ToolStack } from './components/ToolStack';
import { AboutContext } from './components/AboutContext';
import { ModeBanner } from './components/ModeBanner';
import { Contact } from './components/Contact';
import { Footer } from './components/Footer';

const getInitialLocale = (): Locale => {
  if (typeof window !== 'undefined') {
    if (/^\/en(?:\/|$)/i.test(window.location.pathname)) return 'en';
    if (/^\/ru(?:\/|$)/i.test(window.location.pathname)) return 'ru';
  }

  try {
    const saved = localStorage.getItem('dmitry-cv-locale');
    if (saved === 'ru' || saved === 'en') return saved;
  } catch {
    // localStorage may be unavailable
  }

  if (typeof navigator !== 'undefined') {
    const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
    if (languages.some((language) => /^ru(?:-|$)/i.test(language))) return 'ru';
  }

  return 'ru';
};

export const App: React.FC = () => {
  const [locale, setLocale] = useState<Locale>(getInitialLocale);
  const [viewMode, setViewMode] = useState<ViewMode>('30sec');
  const currContent = content[locale] || content.ru;

  useEffect(() => {
    const onPopState = () => setLocale(getInitialLocale());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.setAttribute('data-locale', locale);
    document.title = currContent.meta.title;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) metaDescription.setAttribute('content', currContent.meta.description);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://yakovlefff.ru/${locale}/`;

    try {
      localStorage.setItem('dmitry-cv-locale', locale);
    } catch {
      // ignore
    }
  }, [locale, currContent]);

  const handleLocaleChange = (newLocale: Locale) => {
    setLocale(newLocale);
    if (typeof window !== 'undefined') {
      const nextPath = `/${newLocale}/`;
      if (window.location.pathname !== nextPath) {
        window.history.pushState({ locale: newLocale }, '', `${nextPath}${window.location.hash}`);
      }
    }
  };

  const handleViewModeChange = (newMode: ViewMode) => setViewMode(newMode);

  const handleViewExperience = () => {
    if (viewMode === '30sec') {
      setViewMode('full');
      setTimeout(() => {
        document.getElementById('experience')?.scrollIntoView({ behavior: 'smooth' });
      }, 60);
      return;
    }
    document.getElementById('experience')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="site-wrapper">
      <a className="skip-link" href="#main-content">
        {locale === 'ru' ? 'К содержанию' : 'Skip to content'}
      </a>

      <Hero
        locale={locale}
        onLocaleChange={handleLocaleChange}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        siteContent={currContent}
        onViewExperience={handleViewExperience}
      />

      <main id="main-content">
        {viewMode === '30sec' ? (
          <>
            <ProofScale content={currContent.proofScale} showAll={false} />
            <SelectedWork content={currContent.selectedWork} />
            <PushIncidentFlow content={currContent.pushIncident} />
            <ModeBanner
              viewMode="30sec"
              onToggleMode={() => setViewMode('full')}
              content={currContent.modeBanner}
            />
          </>
        ) : (
          <>
            <ProofScale content={currContent.proofScale} showAll={true} />
            <SelectedWork content={currContent.selectedWork} />
            <PushIncidentFlow content={currContent.pushIncident} />
            <FullExperience content={currContent.fullExperience} />
            <Capabilities content={currContent.capabilities} />
            <Cases content={currContent.casesSection} />
            <Principles content={currContent.principles} />
            <ToolStack content={currContent.toolStack} />
            <AboutContext content={currContent.aboutContext} />
            <ModeBanner
              viewMode="full"
              onToggleMode={() => setViewMode('30sec')}
              content={currContent.modeBanner}
            />
          </>
        )}

        <Contact content={currContent.contact} />
      </main>

      <Footer content={currContent.footer} />
    </div>
  );
};
